/**
 * AES-GCM Symmetric Encryption and Key Wrapping
 */
import { decodeBase64, decodeBase64Url, encodeBase64, encodeBase64Url } from './codec.js';
import { importPrivateKey } from './rsa.js';
/**
 * Encrypt the Private Key string using a symmetric Wrapping Key (AES-GCM)
 */
export async function wrapPrivateKey(privateKeyBase64, wrappingKey) {
    const enc = new TextEncoder();
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encryptedBuffer = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, wrappingKey, enc.encode(privateKeyBase64));
    return {
        iv: encodeBase64(iv),
        ciphertext: encodeBase64(encryptedBuffer),
    };
}
/**
 * Decrypt the Private Key string using the Wrapping Key
 */
export async function unwrapPrivateKey(encrypted, wrappingKey) {
    const iv = decodeBase64(encrypted.iv);
    const ciphertext = decodeBase64(encrypted.ciphertext);
    const decryptedBuffer = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: iv }, wrappingKey, ciphertext);
    const privateKeyBase64 = new TextDecoder().decode(decryptedBuffer);
    return importPrivateKey(privateKeyBase64);
}
/**
 * Encrypt arbitrary text with a symmetric key
 * Output: "iv_base64:ciphertext_base64"
 */
export async function encryptData(text, key) {
    const enc = new TextEncoder();
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, enc.encode(text));
    return `${encodeBase64(iv)}:${encodeBase64(ciphertext)}`;
}
/**
 * Decrypt text encrypted with encryptData
 */
export async function decryptData(encrypted, key) {
    const [ivB64, textB64] = encrypted.split(':');
    if (!ivB64 || !textB64)
        throw new Error('Invalid encrypted format');
    const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: decodeBase64(ivB64) }, key, decodeBase64(textB64));
    return new TextDecoder().decode(decrypted);
}
/**
 * Generate a new Mailbox Master Key (32 random bytes)
 */
export function generateMailboxKey() {
    const key = new Uint8Array(32);
    crypto.getRandomValues(key);
    return key;
}
/**
 * Wrap (encrypt) MMK with a passphrase using PBKDF2 + AES-GCM
 */
export async function wrapMailboxKey(mmk, passphrase, iterations = 50000) {
    const enc = new TextEncoder();
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const keyMaterial = await crypto.subtle.importKey('raw', enc.encode(passphrase), { name: 'PBKDF2' }, false, ['deriveBits', 'deriveKey']);
    const wrappingKey = await crypto.subtle.deriveKey({ name: 'PBKDF2', salt, iterations, hash: 'SHA-256' }, keyMaterial, { name: 'AES-GCM', length: 256 }, false, ['encrypt']);
    const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, wrappingKey, mmk);
    return {
        salt: encodeBase64Url(salt),
        iv: encodeBase64Url(iv),
        ct: encodeBase64Url(ciphertext),
        iters: iterations,
    };
}
/**
 * Serialize WrappedKey to a base64 string for storage/transport.
 * This is the canonical format for km_recovery_wrap.
 */
export function serializeWrappedKey(wrapped) {
    const json = JSON.stringify(wrapped);
    const bytes = new TextEncoder().encode(json);
    return encodeBase64(bytes);
}
/**
 * Deserialize a base64 string back to WrappedKey.
 * Validates required fields.
 */
export function deserializeWrappedKey(serialized) {
    const bytes = decodeBase64(serialized);
    const json = new TextDecoder().decode(bytes);
    const parsed = JSON.parse(json);
    // Validate required fields
    if (typeof parsed.salt !== 'string' ||
        typeof parsed.iv !== 'string' ||
        typeof parsed.ct !== 'string' ||
        typeof parsed.iters !== 'number') {
        throw new Error('Invalid WrappedKey format');
    }
    return parsed;
}
/**
 * Unwrap (decrypt) MMK with a passphrase
 */
export async function unwrapMailboxKey(wrapped, passphrase) {
    const enc = new TextEncoder();
    const salt = decodeBase64Url(wrapped.salt);
    const iv = decodeBase64Url(wrapped.iv);
    const ciphertext = decodeBase64Url(wrapped.ct);
    const keyMaterial = await crypto.subtle.importKey('raw', enc.encode(passphrase), { name: 'PBKDF2' }, false, ['deriveBits', 'deriveKey']);
    const wrappingKey = await crypto.subtle.deriveKey({
        name: 'PBKDF2',
        salt: salt,
        iterations: wrapped.iters,
        hash: 'SHA-256',
    }, keyMaterial, { name: 'AES-GCM', length: 256 }, false, ['decrypt']);
    const mmkBuffer = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: new Uint8Array(iv) }, wrappingKey, new Uint8Array(ciphertext));
    return new Uint8Array(mmkBuffer);
}
/**
 * Encrypt data using MMK with AES-256-GCM and AAD binding
 */
export async function encryptWithMMK(plaintext, mmk, aad) {
    const enc = new TextEncoder();
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const key = await crypto.subtle.importKey('raw', mmk, { name: 'AES-GCM' }, false, ['encrypt']);
    const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv, additionalData: enc.encode(aad) }, key, enc.encode(plaintext));
    const envelope = {
        enc_v: 2,
        alg: 'AES-256-GCM',
        iv: encodeBase64Url(iv),
        ct: encodeBase64Url(ciphertext),
    };
    return encodeBase64Url(enc.encode(JSON.stringify(envelope)));
}
/**
 * Decrypt data using MMK with AES-256-GCM and AAD validation
 */
export async function decryptWithMMK(envelopeBase64, mmk, aad) {
    const dec = new TextDecoder();
    const enc = new TextEncoder();
    const envelopeJson = dec.decode(decodeBase64Url(envelopeBase64));
    const envelope = JSON.parse(envelopeJson);
    if (envelope.enc_v !== 2) {
        throw new Error(`Unsupported envelope version: ${envelope.enc_v}`);
    }
    const iv = decodeBase64Url(envelope.iv);
    const ciphertext = decodeBase64Url(envelope.ct);
    const key = await crypto.subtle.importKey('raw', mmk, { name: 'AES-GCM' }, false, ['decrypt']);
    const plaintext = await crypto.subtle.decrypt({
        name: 'AES-GCM',
        iv: (key ? new Uint8Array(iv) : iv),
        additionalData: enc.encode(aad),
    }, key, new Uint8Array(ciphertext));
    return dec.decode(plaintext);
}
//# sourceMappingURL=aes.js.map